import React, { useEffect, useState } from 'react'
import { Modal, Tab, Nav } from 'react-bootstrap'
import classNames from 'classnames'
import {
  Button,
  useErrorService,
  useNotificationService,
} from '@abroad/components'
import Select from 'react-select'
import makeAnimated from 'react-select/animated'
import SelectionField from './SelectionField'
import { PlatformSchedulingUnavailableContent } from './PlatformSchedulingUnavailableModal'
import API from '../../utils/API'
import { returnSelectionFieldStyle } from '../../constants/common'
import {
  getUniqueCoachSchedulingIssues,
  buildCoachSchedulingFlagsMapFromOptions,
} from '../scheduling/schedulingPreconditions'

const animatedComponents = makeAnimated()

const mapCoachToOption = (coach) => ({
  label: `${coach?.fullName} ${coach?.isStaffCoach ? '(Staff Coach)' : ''}`,
  value: coach?._id,
  isStaffCoach: coach?.isStaffCoach || false,
  fullName: coach?.fullName,
  _id: coach?._id,
  isScheduleEnabled: coach?.isScheduleEnabled,
  hasVideoCallLink: coach?.hasVideoCallLink,
  // TODO: enable bio checks for coach pairing once "Include Bio in the Schedule Email" is added to that flow
  hasCoachBio: coach?.hasCoachBio,
  hasQuestBio: coach?.hasQuestBio,
})

const AssignCoachPairingModal = ({
  fullName = null,
  handleAssignCoach,
  indiSOWCoachIds = [],
  allowedCoachIds = null,
  isSubmitting,
  showModal,
  setShowModal,
  selectedUsers = [],
  userCoachMapping = [],
  onResetAndRefresh = null,
  defaultTab = null,
  hideTabs = false,
}) => {
  const [isLoading, setIsLoading] = useState(true)
  const [coaches, setCoaches] = useState([])
  const [userCoachAssignments, setUserCoachAssignments] = useState({})
  const [activeTab, setActiveTab] = useState(defaultTab || 'client-wise')
  const [coachWiseAssignments, setCoachWiseAssignments] = useState([
    { id: 1, coachId: '', clientIds: [] },
  ])
  const [menuOpenState, setMenuOpenState] = useState({})
  const [modalStep, setModalStep] = useState('pairing')
  const [pendingAssignments, setPendingAssignments] = useState([])
  const [schedulingIssues, setSchedulingIssues] = useState([])
  const ErrorService = useErrorService()
  const Notification = useNotificationService()
  const preserveStateOnCloseRef = React.useRef(false)

  // Track previous showModal state to detect when modal reopens
  const prevShowModalRef = React.useRef(showModal)

  const resetModalState = React.useCallback(() => {
    setCoaches([])
    setUserCoachAssignments({})
    setActiveTab(defaultTab || 'client-wise')
    setCoachWiseAssignments([{ id: 1, coachId: '', clientIds: [] }])
    setMenuOpenState({})
    setModalStep('pairing')
    setPendingAssignments([])
    setSchedulingIssues([])
  }, [defaultTab])

  const onHide = () => {
    setShowModal(false)
    resetModalState()
    preserveStateOnCloseRef.current = false
    // Reset and refresh page when modal is closed by clicking outside
    if (onResetAndRefresh && typeof onResetAndRefresh === 'function') {
      onResetAndRefresh()
    }
  }

  const handleModalHide = () => {
    if (modalStep === 'scheduling-confirm') {
      handleSchedulingConfirmNo()
      return
    }
    onHide()
  }

  useEffect(() => {
    const wasHidden = !prevShowModalRef.current
    const isNowShown = showModal

    if (!showModal) {
      setMenuOpenState({})
      if (prevShowModalRef.current && !preserveStateOnCloseRef.current) {
        resetModalState()
      }
    } else if (wasHidden && isNowShown) {
      if (defaultTab && !preserveStateOnCloseRef.current) {
        setActiveTab(defaultTab)
      }
      if (activeTab === 'client-wise') {
        setCoachWiseAssignments([{ id: 1, coachId: '', clientIds: [] }])
      } else if (activeTab === 'coach-wise') {
        const initialAssignments = {}
        selectedUsers.forEach((user) => {
          initialAssignments[user._id] = ''
        })
        setUserCoachAssignments(initialAssignments)
      }
      preserveStateOnCloseRef.current = false
    }

    // Update ref for next render
    prevShowModalRef.current = showModal
  }, [showModal, activeTab, selectedUsers, resetModalState, defaultTab])

  // reset state when selected users set changes while modal hidden
  const prevSelectedUsersRef = React.useRef(selectedUsers)
  useEffect(() => {
    const prevSelectedUsers = prevSelectedUsersRef.current || []
    const hasChanged =
      prevSelectedUsers.length !== selectedUsers.length ||
      prevSelectedUsers.some(
        (prevUser, idx) => prevUser?._id !== selectedUsers[idx]?._id,
      )

    if (hasChanged && !showModal) {
      resetModalState()
      preserveStateOnCloseRef.current = false
    }

    prevSelectedUsersRef.current = selectedUsers
  }, [selectedUsers, showModal, resetModalState])

  // Initialize user coach assignments when modal first opens (not on reopen)
  useEffect(() => {
    if (showModal && selectedUsers.length > 0) {
      setUserCoachAssignments((prev) => {
        // Only initialize if empty (first time opening)
        if (Object.keys(prev).length === 0) {
          const initialAssignments = {}
          selectedUsers.forEach((user) => {
            initialAssignments[user._id] = ''
          })
          return initialAssignments
        }
        // Preserve existing assignments when returning from Assign SOW
        return prev
      })
    }
  }, [showModal, selectedUsers])

  useEffect(() => {
    setIsLoading(true)
    const getCoachData = async () => {
      try {
        const { data } = await API.AdminAPI.teams.getCoachData()
        if (data) {
          const filteredCoaches = data?.filter(
            (coach) =>
              !indiSOWCoachIds.includes(coach?._id) &&
              (!Array.isArray(allowedCoachIds) ||
                allowedCoachIds.includes(coach?._id)),
          )
          setCoaches(filteredCoaches?.map((coach) => mapCoachToOption(coach)))
        }
        setIsLoading(false)
      } catch (e) {
        ErrorService.showError(e)
        setIsLoading(false)
      }
    }
    if (showModal) getCoachData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal])

  const handleUserCoachChange = (userId, coachId) => {
    setUserCoachAssignments((prev) => ({
      ...prev,
      [userId]: coachId,
    }))
  }

  // Return all assigned coachIds for a given user (may be multiple) preserving
  // the per-user order from user.coaches. If a mapping coach exists and isn't
  // in the list, place it at the beginning.
  const getAssignedCoachIdsForUser = (user) => {
    const orderedIds = []
    if (Array.isArray(user?.coaches)) {
      user.coaches.forEach((c) => {
        if (c?._id && !orderedIds.includes(c._id)) orderedIds.push(c._id)
      })
    }
    const mappingCoachId = userCoachMapping?.find(
      (u) => u?.clientId === user?._id,
    )?.coachId
    if (mappingCoachId && !orderedIds.includes(mappingCoachId)) {
      orderedIds.unshift(mappingCoachId)
    }
    return orderedIds
  }

  // For a given user, move all assigned coaches (if present) to the top
  const getOptionsForUser = (user) => {
    if (!Array.isArray(coaches) || coaches.length === 0) return coaches
    const assignedIds = getAssignedCoachIdsForUser(user)
    if (assignedIds.length === 0) return coaches
    // Build assigned options in the exact order of assignedIds
    const assignedMap = new Map(coaches.map((c) => [c?.value, c]))
    const assignedOrdered = assignedIds
      .map((id) => assignedMap.get(id))
      .filter(Boolean)
    const assignedSet = new Set(assignedIds)
    const rest = coaches.filter((c) => !assignedSet.has(c?.value))
    return [...assignedOrdered, ...rest]
  }

  const proceedToSOWCreation = (assignments) => {
    const coachSchedulingFlagsByCoachId =
      buildCoachSchedulingFlagsMapFromOptions(coaches)
    const issues = getUniqueCoachSchedulingIssues(
      assignments,
      coaches,
      selectedUsers,
    )

    preserveStateOnCloseRef.current = true
    handleAssignCoach(null, coaches, assignments, {
      coachSchedulingFlagsByCoachId,
      forceManualScheduling: issues.length > 0,
    })
    setModalStep('pairing')
    setPendingAssignments([])
    setSchedulingIssues([])
  }

  const handleNext = () => {
    if (activeTab === 'client-wise') {
      const assignments = Object.entries(userCoachAssignments)
        .filter(([userId, coachId]) => coachId !== '')
        .map(([userId, coachId]) => ({ userId, coachId }))

      if (assignments.length === 0) {
        Notification.showNotification(
          'Please select at least one coach assignment',
          'danger',
        )
        return
      }

      const issues = getUniqueCoachSchedulingIssues(
        assignments,
        coaches,
        selectedUsers,
      )

      if (issues.length > 0) {
        setPendingAssignments(assignments)
        setSchedulingIssues(issues)
        setModalStep('scheduling-confirm')
        return
      }

      proceedToSOWCreation(assignments)
    } else {
      // Coach wise assignments
      const validAssignments = coachWiseAssignments.filter(
        (assignment) => assignment.coachId && assignment.clientIds.length > 0,
      )

      if (validAssignments.length === 0) {
        Notification.showNotification(
          'Please select at least one coach and client pairing',
          'danger',
        )
        return
      }

      // Flatten the assignments for each client
      const assignments = []
      validAssignments.forEach((assignment) => {
        assignment.clientIds.forEach((client) => {
          assignments.push({
            userId: client.value,
            coachId: assignment.coachId,
          })
        })
      })

      const issues = getUniqueCoachSchedulingIssues(
        assignments,
        coaches,
        selectedUsers,
      )

      if (issues.length > 0) {
        setPendingAssignments(assignments)
        setSchedulingIssues(issues)
        setModalStep('scheduling-confirm')
        return
      }

      proceedToSOWCreation(assignments)
    }
  }

  const handleSchedulingConfirmNo = () => {
    setModalStep('pairing')
    setPendingAssignments([])
    setSchedulingIssues([])
  }

  const handleSchedulingConfirmYes = () => {
    if (pendingAssignments.length > 0) {
      proceedToSOWCreation(pendingAssignments)
    }
  }

  const handleCoachWiseCoachChange = (index, coachId) => {
    const newAssignments = [...coachWiseAssignments]
    newAssignments[index].coachId = coachId

    // Add new row if both coach and clients are selected
    if (
      coachId &&
      newAssignments[index].clientIds.length > 0 &&
      index === newAssignments.length - 1
    ) {
      newAssignments.push({
        id: newAssignments.length + 1,
        coachId: '',
        clientIds: [],
      })
    }

    setCoachWiseAssignments(newAssignments)
  }

  const handleCoachWiseClientChange = (index, selectedClients) => {
    const newAssignments = [...coachWiseAssignments]
    newAssignments[index].clientIds = selectedClients || []

    // Add new row if both coach and clients are selected
    if (
      newAssignments[index].coachId &&
      selectedClients &&
      selectedClients.length > 0 &&
      index === newAssignments.length - 1
    ) {
      newAssignments.push({
        id: newAssignments.length + 1,
        coachId: '',
        clientIds: [],
      })

      // Close the current row's menu when new row is added
      setMenuOpenState((prev) => ({
        ...prev,
        [index]: false,
      }))
    }

    setCoachWiseAssignments(newAssignments)
  }

  const isAnyCoachSelected =
    activeTab === 'client-wise'
      ? Object.values(userCoachAssignments).some((coachId) => coachId !== '')
      : coachWiseAssignments.some(
          (assignment) => assignment.coachId && assignment.clientIds.length > 0,
        )

  // Get already selected coach IDs from other rows
  const getSelectedCoachIds = (currentIndex) => {
    return coachWiseAssignments
      .filter((_, index) => index !== currentIndex)
      .map((assignment) => assignment.coachId)
      .filter(Boolean)
  }

  // Get already selected client IDs from other rows
  const getSelectedClientIds = (currentIndex) => {
    const selectedIds = new Set()
    coachWiseAssignments.forEach((assignment, index) => {
      if (index !== currentIndex && assignment.clientIds) {
        assignment.clientIds.forEach((client) => {
          selectedIds.add(client.value)
        })
      }
    })
    return Array.from(selectedIds)
  }

  // Get available coaches for a specific row (excluding already selected coaches)
  const getAvailableCoaches = (currentIndex) => {
    const selectedCoachIds = getSelectedCoachIds(currentIndex)
    return coaches.filter((coach) => !selectedCoachIds.includes(coach.value))
  }

  // Get available users for coach-wise multi-select (excluding already selected clients)
  const getUserOptions = (currentIndex) => {
    const selectedClientIds = getSelectedClientIds(currentIndex)
    return selectedUsers
      .filter((user) => !selectedClientIds.includes(user._id))
      .map((user) => ({
        value: user._id,
        label: user.fullName || `${user.fname} ${user.lname}`,
      }))
  }

  const multiSelectStyles = returnSelectionFieldStyle(
    isLoading || isSubmitting,
    selectedUsers?.length,
  )

  if (!showModal) return null

  const isConfirmStep = modalStep === 'scheduling-confirm'

  return (
    <Modal
      show={showModal}
      size={isConfirmStep ? 'lg' : 'xl'}
      onHide={handleModalHide}
      className={classNames('modal-dialog-centered coach-pairing-modal', {
        'payment-modal coach-pairing-modal--confirm-step': isConfirmStep,
      })}
      dialogClassName={
        isConfirmStep
          ? 'payment-modal-dialog justify-content-center'
          : undefined
      }
      centered
      backdrop='static'>
      {isConfirmStep ? (
        <PlatformSchedulingUnavailableContent
          schedulingIssues={schedulingIssues}
          onCancel={handleSchedulingConfirmNo}
          onConfirm={handleSchedulingConfirmYes}
        />
      ) : (
        <Modal.Body className='p-4'>
          <div className='d-flex justify-content-between align-items-center mb-3'>
            <h3 className='s2'>Select Coach Pairing</h3>
            <svg
              className='cursor-pointer'
              onClick={onHide}
              xmlns='http://www.w3.org/2000/svg'
              width='24'
              height='24'
              viewBox='0 0 24 24'
              fill='none'>
              <rect
                x='24'
                y='24'
                width='24'
                height='24'
                rx='12'
                transform='rotate(-180 24 24)'
                fill='#EBEBEB'
              />
              <path d='M17 7L7 17' stroke='black' stroke-linecap='round' />
              <path d='M17 17L7 7' stroke='black' stroke-linecap='round' />
            </svg>
          </div>

          {selectedUsers.length > 0 ? (
            <div>
              <Tab.Container
                activeKey={activeTab}
                onSelect={(k) => setActiveTab(k)}>
                {!hideTabs && (
                  <div className='coach-pairing-tabs mb-3'>
                    <Nav variant='pills'>
                      <Nav.Item>
                        <Nav.Link eventKey='client-wise'>CLIENT WISE</Nav.Link>
                      </Nav.Item>
                      <Nav.Item>
                        <Nav.Link eventKey='coach-wise'>COACH WISE</Nav.Link>
                      </Nav.Item>
                    </Nav>
                  </div>
                )}

                <Tab.Content>
                  <Tab.Pane eventKey='client-wise'>
                    <div className='table-responsive'>
                      <table
                        className='table table-borderless mb-0 table-hover'
                        style={{ tableLayout: 'fixed', width: '100%' }}>
                        <colgroup>
                          <col style={{ width: '50%' }} />
                          <col style={{ width: '50%' }} />
                        </colgroup>
                        <thead>
                          <tr>
                            <th>Client</th>
                            <th>Coach</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedUsers.map((user) => (
                            <tr key={user._id}>
                              <td className='align-middle s5'>
                                {user.fullName || `${user.fname} ${user.lname}`}
                              </td>
                              <td className='align-middle'>
                                <SelectionField
                                  name={`coach-${user._id}`}
                                  size='lg'
                                  options={getOptionsForUser(user)}
                                  getValueAsOption={true}
                                  optionplaceholder={
                                    isLoading
                                      ? 'Fetching Coaches...'
                                      : 'Select Coach'
                                  }
                                  className={classNames(
                                    'w-100 cursor-pointer',
                                    {
                                      'is-disabled': isLoading || isSubmitting,
                                    },
                                  )}
                                  isUseFormikSelectionField={false}
                                  value={userCoachAssignments[user._id] || ''}
                                  onChange={(e) =>
                                    handleUserCoachChange(
                                      user._id,
                                      e.target.value,
                                    )
                                  }
                                  disabled={isLoading || isSubmitting}
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Tab.Pane>

                  <Tab.Pane eventKey='coach-wise'>
                    <div className='coach-wise-container'>
                      <div className='table-responsive'>
                        <table
                          className='table table-borderless mb-0'
                          style={{ tableLayout: 'fixed', width: '100%' }}>
                          <colgroup>
                            <col style={{ width: '50%' }} />
                            <col style={{ width: '50%' }} />
                          </colgroup>
                          <thead>
                            <tr>
                              <th>Coach</th>
                              <th>Clients</th>
                            </tr>
                          </thead>
                          <tbody>
                            {coachWiseAssignments.map((assignment, index) => (
                              <tr key={assignment.id}>
                                <td className='align-middle'>
                                  <SelectionField
                                    name={`coach-wise-coach-${assignment.id}`}
                                    size='lg'
                                    options={getAvailableCoaches(index)}
                                    getValueAsOption={true}
                                    optionplaceholder={
                                      isLoading
                                        ? 'Fetching Coaches...'
                                        : 'Select Coach'
                                    }
                                    className={classNames(
                                      'w-100 cursor-pointer',
                                      {
                                        'is-disabled':
                                          isLoading || isSubmitting,
                                      },
                                    )}
                                    isUseFormikSelectionField={false}
                                    value={assignment.coachId}
                                    onChange={(e) =>
                                      handleCoachWiseCoachChange(
                                        index,
                                        e.target.value,
                                      )
                                    }
                                    disabled={isLoading || isSubmitting}
                                  />
                                </td>
                                <td className='align-middle'>
                                  <Select
                                    value={assignment.clientIds}
                                    closeMenuOnSelect={false}
                                    components={animatedComponents}
                                    isMulti
                                    placeholder='Select Clients'
                                    styles={multiSelectStyles}
                                    onChange={(selectedOptions) =>
                                      handleCoachWiseClientChange(
                                        index,
                                        selectedOptions,
                                      )
                                    }
                                    options={getUserOptions(index)}
                                    isDisabled={isLoading || isSubmitting}
                                    className='coach-wise-multi-select'
                                    isClearable={false}
                                    menuIsOpen={menuOpenState[index]}
                                    onMenuOpen={() =>
                                      setMenuOpenState((prev) => ({
                                        ...prev,
                                        [index]: true,
                                      }))
                                    }
                                    onMenuClose={() =>
                                      setMenuOpenState((prev) => ({
                                        ...prev,
                                        [index]: false,
                                      }))
                                    }
                                    noOptionsMessage={() =>
                                      'No Client available right now'
                                    }
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </Tab.Pane>
                </Tab.Content>
              </Tab.Container>

              <div className='text-center mt-4'>
                <div className='d-flex justify-content-end'>
                  <Button
                    variant='secondary-light'
                    onClick={onHide}
                    className='mr-3 secondary-popup-button px-3 me-3'
                    size='sm'
                    disabled={isLoading}>
                    CANCEL
                  </Button>
                  <Button
                    variant='saffron'
                    className='px-4 py-2'
                    size='md'
                    disabled={isLoading || !isAnyCoachSelected || isSubmitting}
                    isLoading={isSubmitting}
                    onClick={handleNext}>
                    Next
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className='text-center py-4'>
              <p className='text-muted mb-3'>No users selected</p>
              <Button
                variant='secondary'
                className='btn btn-secondary px-4 py-2'
                size='md'
                onClick={onHide}>
                Close
              </Button>
            </div>
          )}
        </Modal.Body>
      )}
    </Modal>
  )
}

export default AssignCoachPairingModal
